import { Diagnostic } from "@fairfleet/geotab";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import debounce from "lodash/debounce";
import React, { useState, useEffect } from "react";
import AddInIds from "@/constants/AddInIds";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { useDiagnosticsBySearch } from "@/hooks/diagnostic/useDiagnosticsBySearch";
import diagnosticsToPreset from "@/hooks/presets/diagnosticsToPreset";
import { Preset } from "@/models/Preset";

export const PresetEditor: React.FC<{
  preset: Partial<Preset> | null;
  onClose: () => void;
  isNew: boolean;
}> = ({ preset, onClose, isNew }) => {
  const updateAddIn = useUpdateAddIn<Preset>(AddInIds.presets, "id");

  const [description, setDescription] = useState(preset?.description || "");
  const [selectedDiagnostics, setSelectedDiagnostics] = useState<Diagnostic[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const {
    data: diagnostics,
    isFetching: diagnosticsFetching,
    error,
  } = useDiagnosticsBySearch(searchQuery, false, 50);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (preset?.columns) {
      setSelectedDiagnostics(
        preset.columns.map(
          col =>
            ({
              id: col.diagnostic.id,
              name: col.diagnostic.name || "Unknown",
            }) as Diagnostic,
        ) || [],
      );
      setDescription(preset.description || "");
    }
  }, [preset]);

  // Debounce the search query to reduce API calls
  const handleSearchChange = debounce((query: string) => {
    setSearchQuery(query);
  }, 300);

  const handleSavePreset = async () => {
    if (!description.trim()) {
      alert("Description is required.");
      return;
    }

    setIsSaving(true);
    try {
      await updateAddIn.mutateAsync(
        diagnosticsToPreset(
          description,
          selectedDiagnostics.map(
            diag =>
              ({
                id: diag.id,
                name: diag.name,
              }) as Diagnostic,
          ),
          preset?.groups?.[0]?.id || "",
        ),
      );
      onClose();
    } catch (error) {
      console.error("Failed to save preset:", error);
      alert("Failed to save preset.");
    } finally {
      setTimeout(() => setIsSaving(false), 2000);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant={"h6"} gutterBottom>
        {isNew ? "Add New Preset" : "Edit Preset"}
      </Typography>
      <TextField
        label={"Preset Description"}
        fullWidth
        value={description}
        onChange={e => setDescription(e.target.value)}
        sx={{ mb: 2 }}
        required
      />

      <Typography variant={"subtitle1"}>{"Search and Select Diagnostics"}</Typography>
      <Box sx={{ mb: 2 }}>
        <Autocomplete
          multiple
          options={diagnostics || []}
          getOptionLabel={option => option.name || "Unknown"}
          getOptionKey={option => option.id}
          loading={diagnosticsFetching}
          value={selectedDiagnostics}
          onChange={(_, newValue) => setSelectedDiagnostics(newValue)}
          filterSelectedOptions
          onInputChange={(_, value) => handleSearchChange(value)}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Checkbox checked={selected} style={{ marginRight: 8 }} />
              <div>
                <Typography variant="body1">{option.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {"ID: " + option.id}
                </Typography>
              </div>
            </li>
          )}
          disableCloseOnSelect
          renderInput={params => (
            <TextField
              {...params}
              label="Search Diagnostics"
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {diagnosticsFetching ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                },
              }}
              error={!!error}
              helperText={error ? "Failed to load diagnostics" : ""}
            />
          )}
        />
      </Box>

      <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
        <Button
          variant={"contained"}
          onClick={handleSavePreset}
          disabled={isSaving}
          startIcon={isSaving ? <CircularProgress size={20} /> : null}
        >
          {"Save"}
        </Button>
        <Button variant={"outlined"} onClick={onClose}>
          {"Cancel"}
        </Button>
      </Box>
    </Box>
  );
};
